<template>
  <div v-if="title" :class="['nav_depth02', { open: isMenuOpen }]">
    <div class="inr">
      <div class="title">{{ title }}</div>
      <button class="btn_nav_depth02" @click.prevent="toggleDepth02">{{ currentButtonTitle }}</button>
      <div class="lst">
        <ul>
          <li v-for="child in visibleMenus" :key="child.menuId">
            <a
              v-if="/^https?:\/\//i.test(hrefForMenu(child))"
              :href="hrefForMenu(child)"
              target="_blank"
              :rel="relFor(child)"
              :class="{ on: isActive(child) }"
            >
              <span><span>{{ child.menuNm }}</span><span></span></span>
            </a>
            <RouterLink
              v-else
              :to="hrefForMenu(child) || '#'"
              :target="child.target"
              :rel="relFor(child)"
              :class="{ on: isActive(child) }"
              @click.prevent="navigate(child)"
            >
              <span><span>{{ child.menuNm }}</span><span></span></span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { countMenuAccess } from '@/service/stats.service'

defineOptions({ name: 'SubNav' })

const props = defineProps({
  menus: { type: Array, default: () => [] },
  currentMenu: { type: Object, default: null },
  title: { type: String, default: '' },
})

const emit = defineEmits(['menu-change'])

const route = useRoute()
const router = useRouter()
const clickedMenuId = ref(null)
const isMenuOpen = ref(false)

const toggleDepth02 = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const isVisibleMenu = (menu) =>
  menu?.menuIndctYn === 'Y' || menu?.menuIndctYn === 'YES'

const visibleMenus = computed(() => {
  const list = Array.isArray(props.menus) ? props.menus : []
  return list.filter(isVisibleMenu)
})

const currentButtonTitle = computed(() => {
  return props.currentMenu?.menuNm || props.title || '전체'
})

watch(
  () => route.fullPath,
  () => {
    clickedMenuId.value = null
    isMenuOpen.value = false
  },
)

const normalizeFullPath = (raw) => {
  if (!raw) return ''
  try {
    const url = new URL(raw, window.location.origin)
    const params = new URLSearchParams(url.search)
    const ordered = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b))
    const sortedSearch = ordered.length
      ? `?${ordered.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`
      : ''
    const pathname = url.pathname.replace(/\/$/, '')
    return `${pathname}${sortedSearch}`
  } catch {
    return String(raw).replace(/\/$/, '')
  }
}

const currentFullPath = computed(() => normalizeFullPath(route.fullPath))

const normalizeLink = (raw) => {
  const link = String(raw || '').trim()
  if (!link || link === 'dir' || link === 'N') return null
  if (/^https?:\/\//i.test(link)) return link
  return link.startsWith('/') ? link : `/${link}`
}

const getMenuType = (menu) => String(menu?.menuType || '').toUpperCase()

const findFirstNavigableChild = (menu) => {
  const children = Array.isArray(menu?.children) ? menu.children : []
  for (const child of children) {
    const href = hrefForMenu(child)
    if (href) return child
  }
  return null
}

const hrefForMenu = (menu) => {
  if (!menu) return null
  const menuType = getMenuType(menu)
  if (menuType === 'DIR') {
    const first = findFirstNavigableChild(menu)
    return first ? hrefForMenu(first) : null
  }
  if (menuType === 'CONTENT') {
    const link = String(menu.menuLink || '').trim()
    if (!link || link === 'dir' || link === 'N') return null
    const normalized = link.startsWith('/') ? link : `/${link}`
    return normalized.startsWith('/cms/') ? normalized : `/cms${normalized}`
  }
  return normalizeLink(menu?.path) || normalizeLink(menu?.menuLink)
}

const navigate = async (menu) => {
  if (!menu) return
  const menuType = getMenuType(menu)
  if (menuType === 'DIR') {
    const first = findFirstNavigableChild(menu)
    if (first) {
      await navigate(first)
    }
    return
  }

  // 메뉴 접속수 카운트
  if (menu.menuId) {
    const href = hrefForMenu(menu)
    countMenuAccess({ menuUrl: href || route.path, menuId: menu.menuId }).catch(() => {})
  }

  if (menuType === 'CONTENT') {
    const link = String(menu.menuLink || '').trim()
    if (!link || link === 'dir' || link === 'N') return
    const normalized = link.startsWith('/') ? link : `/${link}`
    const target = normalized.startsWith('/cms/') ? normalized : `/cms${normalized}`
    clickedMenuId.value = menu.menuId
    emit('menu-change', menu.menuId)
    router.push({ path: target, query: { menuId: menu.menuId, _t: Date.now() } })
    return
  }

  const href = hrefForMenu(menu)
  if (!href) return
  clickedMenuId.value = menu.menuId
  if (/^https?:\/\//i.test(href)) {
    window.location.href = href
    return
  }

  // subNav 메뉴 클릭 시 SPA 라우팅 (하드 리프레시 → router.push 전환)
  // - 같은 메뉴 재클릭 시 _t(타임스탬프)로 강제 re-mount → 검색조건/페이징 초기화
  emit('menu-change', menu.menuId)
  router.push({ path: href, query: { menuId: menu.menuId, _t: Date.now() } })
}

const relFor = (item) => item?.rel || (item?.target === '_blank' ? 'noopener noreferrer' : undefined)

const currentMenuId = computed(() => props.currentMenu?.menuId)
const stripSlash = (p = '') => String(p).replace(/\/$/, '')

const getPathname = (raw) => {
  if (!raw) return ''
  try {
    return stripSlash(new URL(raw, window.location.origin).pathname)
  } catch {
    return stripSlash(String(raw))
  }
}

function isActive(child) {
  const href = hrefForMenu(child)
  if (clickedMenuId.value === child.menuId) return true

  if (href) {
    const hrefPath = getPathname(href)
    const routePath = stripSlash(route.path)

    if (hrefPath === routePath) return true

    // const normalizedHref = normalizeFullPath(href)
    // if (normalizedHref === currentFullPath.value) return true
  }

  return currentMenuId.value === child.menuId
}
</script>
